import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Key, Plus, Trash2, Copy, Power, Globe, Server, Pencil } from "lucide-react";

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [parking, setParking] = useState(null);
  const [zoneDefaults, setZoneDefaults] = useState(null);
  const [savingZd, setSavingZd] = useState(false);
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRate, setNewRate] = useState(60);
  const [newScope, setNewScope] = useState("park");
  const [creating, setCreating] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editScope, setEditScope] = useState("park");
  const [editRate, setEditRate] = useState(60);
  const [savingEdit, setSavingEdit] = useState(false);

  // Secret reveal dialog
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealed, setRevealed] = useState(null);

  // Parking config form
  const [savingPark, setSavingPark] = useState(false);

  const load = async () => {
    try {
      const [k, p, zd] = await Promise.all([
        api.get("/api-keys"),
        api.get("/settings/parking"),
        api.get("/settings/zone-defaults"),
      ]);
      setKeys(Array.isArray(k.data) ? k.data : []);
      setParking(p.data);
      setZoneDefaults(zd.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createKey = async () => {
    if (!newName.trim()) { toast.error("İsim gerekli"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/api-keys", {
        name: newName.trim(),
        scope: newScope,
        rate_limit_per_min: Number(newRate) || 60,
      });
      setRevealed(data);
      setRevealOpen(true);
      setCreateOpen(false);
      setNewName("");
      setNewRate(60);
      setNewScope("park");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const toggleKey = async (k) => {
    try {
      await api.patch(`/api-keys/${k.id}?is_active=${!k.is_active}`);
      toast.success(k.is_active ? "Devre dışı bırakıldı" : "Aktive edildi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (k) => {
    setEditId(k.id);
    setEditName(k.name);
    setEditScope(k.scope);
    setEditRate(k.rate_limit_per_min);
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editName.trim()) { toast.error("İsim gerekli"); return; }
    setSavingEdit(true);
    try {
      await api.put(`/api-keys/${editId}`, {
        name: editName.trim(),
        scope: editScope,
        rate_limit_per_min: Number(editRate) || 60,
      });
      toast.success("Anahtar güncellendi");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteKey = async (k) => {
    if (!window.confirm(`"${k.name}" anahtarını sil? Bu anahtar ile artık park API çağrılamaz.`)) return;
    try {
      await api.delete(`/api-keys/${k.id}`);
      toast.success("Anahtar silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt).then(
      () => toast.success("Kopyalandı"),
      () => toast.error("Kopyalanamadı"),
    );
  };

  const updateParking = async (patch) => {
    setParking((p) => ({ ...p, ...patch }));
  };

  const updateZd = (patch) => setZoneDefaults((z) => ({ ...z, ...patch }));

  const saveZoneDefaults = async () => {
    if (!zoneDefaults) return;
    setSavingZd(true);
    try {
      const nameservers = Array.isArray(zoneDefaults.nameservers)
        ? zoneDefaults.nameservers
        : String(zoneDefaults.nameservers || "").split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put("/settings/zone-defaults", { ...zoneDefaults, nameservers });
      setZoneDefaults(data);
      toast.success("Zone varsayılanları kaydedildi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingZd(false);
    }
  };

  const saveParking = async () => {
    if (!parking) return;
    setSavingPark(true);
    try {
      const nameservers = Array.isArray(parking.nameservers)
        ? parking.nameservers
        : String(parking.nameservers || "").split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      const body = { ...parking, nameservers };
      const { data } = await api.put("/settings/parking", body);
      setParking(data);
      toast.success("Park ayarları kaydedildi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingPark(false);
    }
  };

  const exampleCreate = parking ? `# Zone oluştur (park et)
curl -X POST https://dns.netlen.com.tr/api/public/park-domain \\
  -H "X-API-Key: nlk_XXXXXXXX_XXXXXXXXXXXX" \\
  -H "Content-Type: application/json" \\
  -d '{"domain":"musteri.com"}'` : "";

  const exampleDelete = `# Zone sil (park iptal)
curl -X DELETE https://dns.netlen.com.tr/api/public/park-domain/musteri.com \\
  -H "X-API-Key: nlk_XXXXXXXX_XXXXXXXXXXXX"`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Key size={22} /> API & Park
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Dış sistemler için API anahtarları ve park domain şablonu
        </p>
      </div>

      {/* Parking Template */}
      <div className="border border-border bg-card rounded-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold flex items-center gap-2"><Globe size={15} /> Park Zone Şablonu</h2>
          <Button size="sm" onClick={saveParking} disabled={savingPark || !parking} data-testid="park-save">
            {savingPark ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
        {parking ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Nameserver Kayıtları</Label>
              <textarea
                value={Array.isArray(parking.nameservers) ? parking.nameservers.join("\n") : (parking.nameservers || "")}
                onChange={(e) => updateParking({ nameservers: e.target.value.split("\n") })}
                rows={4}
                className="w-full mt-1 text-sm font-mono bg-background border border-border rounded-sm p-2 focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="ns1.netlen.com."
                data-testid="park-ns"
              />
              <p className="text-[10px] text-muted-foreground font-mono mt-1">Her satıra bir NS (sonda noktalı)</p>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">IPv4 (A) — apex + www</Label>
                <Input className="mt-1 font-mono" value={parking.ipv4 || ""} onChange={(e) => updateParking({ ipv4: e.target.value })} placeholder="212.87.198.2" data-testid="park-ipv4" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">IPv6 (AAAA) — apex + www</Label>
                <Input className="mt-1 font-mono" value={parking.ipv6 || ""} onChange={(e) => updateParking({ ipv6: e.target.value })} placeholder="2a01:f900:10:1::1" data-testid="park-ipv6" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">A/AAAA TTL</Label>
                  <Input type="number" className="mt-1 font-mono" value={parking.ttl || 300} onChange={(e) => updateParking({ ttl: Number(e.target.value) || 300 })} />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">NS TTL</Label>
                  <Input type="number" className="mt-1 font-mono" value={parking.ns_ttl || 86400} onChange={(e) => updateParking({ ns_ttl: Number(e.target.value) || 86400 })} />
                </div>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">SOA Primary</Label>
                <Input className="mt-1 font-mono" value={parking.soa_primary || ""} onChange={(e) => updateParking({ soa_primary: e.target.value })} placeholder="ns1.netlen.com." />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">SOA Mailbox</Label>
                <Input className="mt-1 font-mono" value={parking.soa_mailbox || ""} onChange={(e) => updateParking({ soa_mailbox: e.target.value })} placeholder="hostmaster.netlen.com." />
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground font-mono">yükleniyor...</div>
        )}
      </div>

      {/* Zone defaults (manual + advanced) */}
      <div className="border border-border bg-card rounded-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold flex items-center gap-2"><Server size={15} /> Zone Varsayılanları (NS + SOA)</h2>
          <Button size="sm" onClick={saveZoneDefaults} disabled={savingZd || !zoneDefaults} data-testid="zd-save">
            {savingZd ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Manuel ve Advanced zone'lar oluşturulurken kullanılan varsayılan NS ve SOA değerleri. (Park şablonu ayrıdır.)
        </p>
        {zoneDefaults ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Nameserver Kayıtları</Label>
              <textarea
                value={Array.isArray(zoneDefaults.nameservers) ? zoneDefaults.nameservers.join("\n") : (zoneDefaults.nameservers || "")}
                onChange={(e) => updateZd({ nameservers: e.target.value.split("\n") })}
                rows={4}
                className="w-full mt-1 text-sm font-mono bg-background border border-border rounded-sm p-2 focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="ns1.tresteknoloji.com."
                data-testid="zd-ns"
              />
              <p className="text-[10px] text-muted-foreground font-mono mt-1">Her satıra bir NS (sonda noktalı)</p>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">SOA Primary (MNAME)</Label>
                <Input className="mt-1 font-mono" value={zoneDefaults.soa_primary || ""} onChange={(e) => updateZd({ soa_primary: e.target.value })} placeholder="ns1.tresteknoloji.com." data-testid="zd-soa-primary" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">SOA Hostmaster (RNAME)</Label>
                <Input className="mt-1 font-mono" value={zoneDefaults.soa_mailbox || ""} onChange={(e) => updateZd({ soa_mailbox: e.target.value })} placeholder="hostmaster.tresteknoloji.com." data-testid="zd-soa-mailbox" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">NS TTL</Label>
                <Input type="number" className="mt-1 font-mono" value={zoneDefaults.ns_ttl || 86400} onChange={(e) => updateZd({ ns_ttl: Number(e.target.value) || 86400 })} data-testid="zd-ns-ttl" />
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground font-mono">yükleniyor...</div>
        )}
      </div>

      {/* Usage example */}
      <div className="border border-border bg-card rounded-md p-5">
        <h2 className="text-sm font-semibold mb-3">Kullanım Örnekleri</h2>
        <div className="space-y-3">
          <div className="relative">
            <pre className="text-[11px] font-mono bg-background border border-border rounded-sm p-3 overflow-x-auto whitespace-pre">{exampleCreate}</pre>
            <Button size="sm" variant="ghost" className="absolute top-2 right-2" onClick={() => copy(exampleCreate)}>
              <Copy size={12} />
            </Button>
          </div>
          <div className="relative">
            <pre className="text-[11px] font-mono bg-background border border-border rounded-sm p-3 overflow-x-auto whitespace-pre">{exampleDelete}</pre>
            <Button size="sm" variant="ghost" className="absolute top-2 right-2" onClick={() => copy(exampleDelete)}>
              <Copy size={12} />
            </Button>
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground font-mono mt-3 space-y-0.5">
          <div>• POST başarılı cevap: <span className="text-foreground">{"{ok:true, status:'created'|'exists', zone_id, pdns_targets:[...]}"}</span></div>
          <div>• POST tekrar edilirse: <span className="text-foreground">status:'exists'</span> (idempotent, mevcut zone'a dokunmaz)</div>
          <div>• DELETE başarılı: <span className="text-foreground">{"{ok:true, status:'deleted'|'not_found'}"}</span> — zone yoksa 'not_found' döner (idempotent)</div>
          <div>• DELETE güvenlik: park API yalnızca <span className="text-foreground">source='parking'</span> zone'larını silebilir (WHM/manuel korumalı → 403)</div>
          <div>• Rate limit: anahtar başına dakikada <span className="text-foreground">60</span> istek (özelleştirilebilir)</div>
        </div>
      </div>

      {/* API Keys list */}
      <div className="border border-border bg-card rounded-md">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-sm font-semibold">API Anahtarları</h2>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="key-new">
            <Plus size={13} className="mr-1.5" /> Yeni Anahtar
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="keys-table">
            <thead>
              <tr>
                <th>İsim</th>
                <th>Prefix</th>
                <th>Scope</th>
                <th>Rate Limit</th>
                <th>Kullanım</th>
                <th>Son Kullanım</th>
                <th>Durum</th>
                <th className="text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-8 text-muted-foreground text-sm">yükleniyor...</td></tr>}
              {!loading && keys.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-muted-foreground text-sm font-mono">anahtar yok — "Yeni Anahtar" ile ekle</td></tr>}
              {keys.map((k) => (
                <tr key={k.id}>
                  <td className="text-sm font-semibold">{k.name}</td>
                  <td className="font-mono text-xs">{k.prefix}_••••••••</td>
                  <td><span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded-sm bg-accent">{k.scope}</span></td>
                  <td className="font-mono text-xs">{k.rate_limit_per_min}/dk</td>
                  <td className="font-mono text-xs">{k.use_count}</td>
                  <td className="font-mono text-xs text-muted-foreground">{k.last_used_at ? new Date(k.last_used_at).toLocaleString("tr-TR") : "—"}</td>
                  <td>
                    <span className={`status-badge ${k.is_active ? "ok" : "muted"}`}>
                      {k.is_active ? "aktif" : "pasif"}
                    </span>
                  </td>
                  <td>
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(k)} title="Düzenle">
                        <Pencil size={13} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleKey(k)} title={k.is_active ? "Devre dışı bırak" : "Aktive et"}>
                        <Power size={13} className={k.is_active ? "text-emerald-400" : "text-muted-foreground"} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteKey(k)} className="text-red-400 hover:text-red-300">
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="key-create-dialog">
          <DialogHeader>
            <DialogTitle>Yeni API Anahtarı</DialogTitle>
            <DialogDescription>Park domain API'sini çağıran dış sistemler için.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">İsim</Label>
              <Input className="mt-1" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Panel müşteri ekleme" data-testid="key-name" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Yetki (scope)</Label>
              <select
                className="mt-1 w-full h-10 rounded-md border border-border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
                value={newScope}
                onChange={(e) => setNewScope(e.target.value)}
                data-testid="key-scope"
              >
                <option value="park">park — sadece park domain oluştur/sil</option>
                <option value="advanced">advanced — tam zone + record yönetimi</option>
                <option value="metrics">metrics — resolver istatistik gönderimi (ingest)</option>
              </select>
              <div className="mt-1 text-[11px] text-muted-foreground">
                {newScope === "advanced"
                  ? "Zone oluşturma, A/TXT/CNAME/MX… ekleme-düzenleme-silme, zone silme."
                  : newScope === "metrics"
                  ? "Sadece resolver istatistiklerini panele gönderme (POST /api/resolver/ingest)."
                  : "Yalnızca hazır şablonla park domain oluşturma ve silme."}
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Rate Limit (dakika başına)</Label>
              <Input type="number" className="mt-1 font-mono" value={newRate} onChange={(e) => setNewRate(e.target.value)} min="1" max="1000" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>Vazgeç</Button>
            <Button onClick={createKey} disabled={creating} data-testid="key-create-submit">
              {creating ? "Oluşturuluyor..." : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog — isim / scope / rate limit */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="key-edit-dialog">
          <DialogHeader>
            <DialogTitle>Anahtarı Düzenle</DialogTitle>
            <DialogDescription>İsim, yetki ve rate limit değiştirilebilir. Anahtarın kendisi değişmez.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">İsim</Label>
              <Input className="mt-1" value={editName} onChange={(e) => setEditName(e.target.value)} data-testid="key-edit-name" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Yetki (scope)</Label>
              <select
                className="mt-1 w-full h-10 rounded-md border border-border bg-background px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring"
                value={editScope}
                onChange={(e) => setEditScope(e.target.value)}
                data-testid="key-edit-scope"
              >
                <option value="park">park — sadece park domain oluştur/sil</option>
                <option value="advanced">advanced — tam zone + record yönetimi</option>
                <option value="metrics">metrics — resolver istatistik gönderimi (ingest)</option>
                <option value="admin">admin — tüm scope'lar</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Rate Limit (dakika başına)</Label>
              <Input type="number" className="mt-1 font-mono" value={editRate} onChange={(e) => setEditRate(e.target.value)} min="1" max="1000" data-testid="key-edit-rate" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={savingEdit}>Vazgeç</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="key-edit-submit">
              {savingEdit ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal dialog — shown ONCE after create */}
      <Dialog open={revealOpen} onOpenChange={setRevealOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-amber-400">Anahtarı şimdi kopyalayın</DialogTitle>
            <DialogDescription>
              Bu tam anahtar yalnızca bir kez gösterilir. Şifrelenerek saklanır ve sonra okunamaz.
            </DialogDescription>
          </DialogHeader>
          {revealed && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Input readOnly value={revealed.full_key} className="font-mono text-xs" data-testid="revealed-key" />
                <Button size="sm" onClick={() => copy(revealed.full_key)} data-testid="revealed-copy">
                  <Copy size={13} className="mr-1.5" /> Kopyala
                </Button>
              </div>
              <div className="text-[11px] font-mono text-muted-foreground space-y-1">
                <div>İsim: <span className="text-foreground">{revealed.name}</span></div>
                <div>Prefix: <span className="text-foreground">{revealed.prefix}</span></div>
                <div>Scope: <span className="text-foreground">{revealed.scope}</span> · Rate limit: <span className="text-foreground">{revealed.rate_limit_per_min}/dk</span></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setRevealOpen(false)}>Anladım, kopyaladım</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
